/*
  Copyright 2019 Bit Factory, Inc.
  This Source Code Form is subject to the terms of the Mozilla Public
  License, v. 2.0. If a copy of the MPL was not distributed with this
  file, You can obtain one at http://mozilla.org/MPL/2.0/.
*/
import Web3 from 'web3-old';
import { ProviderMethod } from './method';
import platform from 'platform';
const ethSigUtil = require('eth-sig-util');
// HttpProvider: https://github.com/ethereum/web3.js/blob/v0.20.6/lib/web3/httpprovider.js
export class QurageLinkWeb3Provider extends Web3.providers.HttpProvider {
    constructor(endpoint, options) {
        super(endpoint);
        this.address = options.address;
        this.firebase = options.backend;
        this.deepLink = options.deepLink;
        this.rpcClient = options.rpcClient;
        this.netVersion = options.netVersion;
    }
    send(payload, callback) {
        const method = payload.method;
        switch (method) {
            case ProviderMethod.EthAccounts:
            case ProviderMethod.EthCoinbase:
                return callback(null, this[method](payload));
            default:
                return super.send(payload, callback);
        }
    }
    // TODO: 将来的に対応が必要そうなメソッド
    // eth_newFilter
    // eth_newBlockFilter
    // eth_newPendingTransactionFilter
    // eth_uninstallFilter
    // eth_getFilterChanges
    // eth_getFilterLogs
    async sendAsync(payload, callback) {
        const method = payload.method;
        switch (method) {
            case ProviderMethod.EthAccounts:
            case ProviderMethod.EthCoinbase:
            case ProviderMethod.NetVersion:
                return callback(null, this[method](payload));
            case ProviderMethod.EthSendTransaction:
            case ProviderMethod.EthSign:
            case ProviderMethod.PersonalECRecover:
            case ProviderMethod.PersonalSign:
                return this[method](payload)
                    .then(result => callback(null, result))
                    .catch(err => callback(new Error(err), QurageLinkWeb3Provider.createResponse(payload, null)));
            case ProviderMethod.EthCall:
            case ProviderMethod.EthEstimateGas:
            case ProviderMethod.EthGasPrice:
            case ProviderMethod.EthGetBalance:
            case ProviderMethod.EthGetBlockByNumber:
            case ProviderMethod.EthGetTransactionReceipt:
                return this.rpcClient.post(payload)
                    .then(result => callback(null, result))
                    .catch(err => callback(new Error(err), QurageLinkWeb3Provider.createResponse(payload, null)));
            default:
                return callback(new Error(`Not supported method: ${method}`), QurageLinkWeb3Provider.createResponse(payload, null));
        }
    }
    [ProviderMethod.NetVersion](payload) {
        return QurageLinkWeb3Provider.createResponse(payload, this.netVersion);
    }
    [ProviderMethod.EthAccounts](payload) {
        return QurageLinkWeb3Provider.createResponse(payload, [this.address]);
    }
    [ProviderMethod.EthCoinbase](payload) {
        return QurageLinkWeb3Provider.createResponse(payload, [this.address]);
    }
    async [ProviderMethod.EthSign](payload) {
        const { signedRes } = await this.requestSign(payload);
        return QurageLinkWeb3Provider.createResponse(payload, signedRes);
    }
    async [ProviderMethod.EthSendTransaction](payload) {
        const { signedRes } = await this.requestSign(payload);
        return QurageLinkWeb3Provider.createResponse(payload, signedRes);
    }
    // https://github.com/ethereum/go-ethereum/wiki/Management-APIs#personal_ecrecover
    [ProviderMethod.PersonalECRecover](payload) {
        return new Promise(resolve => {
            const [data, sig] = payload.params;
            const recovered = ethSigUtil.recoverPersonalSignature({ data, sig });
            resolve(QurageLinkWeb3Provider.createResponse(payload, recovered));
        });
    }
    // https://github.com/ethereum/go-ethereum/wiki/Management-APIs#personal_sign
    async [ProviderMethod.PersonalSign](payload) {
        const { signedRes } = await this.requestSign(payload);
        return QurageLinkWeb3Provider.createResponse(payload, signedRes);
    }
    async requestSign(payload) {
        const user = this.firebase.user;
        if (!user) {
            return Promise.reject('user not found');
        }
        if (user.isDeepLink && platform.os.family.includes('iOS')) {
            if (platform.os.family.includes('iOS')) {
                window.open('', 'qurage-link');
            }
        }
        const { docId } = await this.firebase
            .send(payload, !user.isDeepLink);
        if (user.isDeepLink) {
            this.deepLink.sign(docId);
        }
        return this.firebase
            .watchSignedMessage(docId)
            .catch(error => {
            throw new Error(error);
        });
    }
    static createResponse(payload, result) {
        return {
            id: payload.id,
            jsonrpc: '2.0',
            result: result
        };
    }
}
